<template>
  <div ref="monacoContainer" class="monaco-editor-container"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import loader from '@monaco-editor/loader';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  language: {
    type: String,
    default: 'json'
  },
  theme: {
    type: String,
    default: 'vs-dark'
  },
  height: {
    type: String,
    default: '200px'
  }
});

const emit = defineEmits(['update:modelValue']);

const monacoContainer = ref(null);
let monacoEditorInstance = null;

const initMonaco = async () => {
  if (monacoContainer.value && !monacoEditorInstance) {
    try {
      const monaco = await loader.init();
      monacoEditorInstance = monaco.editor.create(monacoContainer.value, {
        value: props.modelValue,
        language: props.language,
        theme: props.theme,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        fontSize: 12,
        lineHeight: 18,
        padding: { top: 6, bottom: 6 }
      });

      monacoEditorInstance.onDidChangeModelContent(() => {
        if (monacoEditorInstance !== null && monacoEditorInstance !== undefined) {
          const val = monacoEditorInstance.getValue();
          if (val !== props.modelValue) {
            emit('update:modelValue', val);
          }
        }
      });
    } catch (error) {
      console.error("Failed to initialize Monaco editor:", error);
    }
  }
};

const destroyMonaco = () => {
  if (monacoEditorInstance !== null && monacoEditorInstance !== undefined) {
    monacoEditorInstance.dispose();
    monacoEditorInstance = null;
  }
};

watch(() => props.modelValue, (newVal) => {
  if (monacoEditorInstance !== null && monacoEditorInstance !== undefined && monacoEditorInstance.getValue() !== newVal) {
    monacoEditorInstance.setValue(newVal);
  }
});

onMounted(() => {
  nextTick(() => {
    initMonaco();
  });
});

onUnmounted(() => {
  destroyMonaco();
});
</script>

<style scoped>
.monaco-editor-container {
  width: 100%;
  height: v-bind('props.height');
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}
</style>
